/**
 * Bada skin quiz: <bada-skin-quiz-component> in sections/bada-skin-quiz.liquid.
 * Port of prototype/assets/quiz.js (approved design). The section renders the intro screen and a JSON payload
 * (translated copy, the five skin type collections, compact in-stock products). This module renders the seven
 * question screens and the result screen, and picks one real product per routine phase.
 *
 * Storage on this device:
 *   bada-quiz-v1       progress { v, step, answers, done, taken_at }; lets a shopper resume
 *   bada-skin-profile  local copy of customer.metafields.bada.skin_profile, written only after the app saved it
 *   bada-skin-offer    local copy of customer.metafields.bada.skin_offer, written only after the app returned it
 *                      (both carry `owner`, the signed-in customer id, so the widget never shows them to another
 *                      shopper on this device)
 *
 * Saving (never a direct metafield write from the browser, never a made-up code):
 *   1. "Save my result" dispatches the cancelable, bubbling event `bada:skin-quiz:save` with
 *      detail { profile: { type, sensitivity, concerns, quiz_version }, collection, percent, days, respond(result) }.
 *      The Bada app embed may take over (event.preventDefault()) and later call detail.respond(result); an answer
 *      that arrives after "Retake the quiz" belongs to the cleared answers and is ignored.
 *   2. Otherwise, when the section has an app proxy path: guests go to sign in and come back with ?save=1
 *      (new customer accounts: /customer_authentication/login?return_to=<this page>?save=1, plus a 30 minute
 *      "save after sign-in" note on this device in case the query does not survive the round trip); the result
 *      then saves on its own. Signed-in customers POST the profile to the proxy. Expected JSON answer (contract
 *      for the Bada app): { saved: boolean, offer: { code, collection, percent, starts_at, expires_at,
 *      redeemed_at } | null }; 401 { reason: "sign_in" } when the app sees no signed-in customer (the next
 *      manual save then goes through sign-in, an automatic save never does).
 *      Not connected yet (404, 501, or a page that is not JSON): "saving is not available", never a code.
 *   3. Without either, the card says saving is not available. No code is ever shown unless the app sent it for
 *      the skin type collection the card advertises, or it comes from customer.metafields.bada.skin_offer
 *      (rendered by the section).
 *
 * Copy: every string in the payload is the output of the Liquid t filter (HTML-escaped text). `fill()` replaces
 * {name} tokens; product data is escaped here. Count strings arrive as plural forms (copy.plurals: every form the
 * locale file has) and #plural() picks one with Intl.PluralRules. No scroll listeners. Motion only under
 * prefers-reduced-motion: no-preference (CSS). Logical properties only.
 */
import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const QUIZ_VERSION = 1;
const KEY_PROGRESS = 'bada-quiz-v1';
const KEY_PROFILE = 'bada-skin-profile';
const KEY_OFFER = 'bada-skin-offer';
const KEY_SAVE_INTENT = 'bada-quiz-save-intent';
const SAVE_INTENT_MS = 30 * 60 * 1000;
const SAVE_EVENT = 'bada:skin-quiz:save';

const store = {
  /** @param {string} key @param {any} [fallback] */
  get(key, fallback = null) {
    try {
      return JSON.parse(localStorage.getItem(key) ?? 'null') ?? fallback;
    } catch {
      return fallback;
    }
  },
  /** @param {string} key @param {any} value */
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode: the quiz still works, it just cannot resume */
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing stored */
    }
  },
};

/**
 * Reads and clears the "save after sign-in" note left by saveResult() before it sent a guest to sign in.
 * @returns {boolean} true when the note is younger than SAVE_INTENT_MS
 */
const takeSaveIntent = () => {
  const intent = store.get(KEY_SAVE_INTENT);
  store.remove(KEY_SAVE_INTENT);
  const at = Number(intent?.at);
  return Number.isFinite(at) && Date.now() - at >= 0 && Date.now() - at < SAVE_INTENT_MS;
};

/** @param {unknown} value */
const esc = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Fills {name} tokens in a translated string. `values` are plain text (escaped here), `raw` are trusted markup or
 * other translated strings (already escaped by the t filter).
 * @param {string} template
 * @param {Record<string, unknown>} [values]
 * @param {Record<string, string>} [raw]
 */
const fill = (template, values = {}, raw = {}) =>
  String(template ?? '').replace(/\{(\w+)\}/g, (match, name) => {
    if (name in raw) return String(raw[name]);
    if (name in values) return esc(values[name]);
    return match;
  });

/** @returns {{ cart_add_url: string, cart_url: string }} */
const themeRoutes = () =>
  // `Theme` is the global declared in snippets/scripts.liquid
  // eslint-disable-next-line no-undef
  (typeof Theme !== 'undefined' && Theme?.routes) || { cart_add_url: '/cart/add.js', cart_url: '/cart' };

async function refreshCart() {
  /** @type {any} */
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }
  const response = await fetch(`${themeRoutes().cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/* ---------- Questions (7, one per screen). Structure here, copy from the section payload ---------- */
const CONCERN_KEYS = ['hydration', 'acne', 'pores', 'brightening', 'firmness', 'redness'];
const QUESTIONS = [
  { id: 'feel', type: 'radio', options: ['tight', 'comfortable', 'tzone', 'shiny'] },
  { id: 'pores', type: 'radio', options: ['small', 'nose', 'tzone', 'most'] },
  { id: 'react', type: 'radio', options: ['rarely', 'sometimes', 'often'] },
  { id: 'goals', type: 'checkbox', max: 2, options: CONCERN_KEYS },
  { id: 'climate', type: 'radio', options: ['humid', 'cold', 'seasonal', 'mild'] },
  { id: 'length', type: 'radio', options: ['three', 'five', 'full'] },
  { id: 'sun', type: 'radio', options: ['daily', 'sometimes', 'notyet'] },
];
const N = QUESTIONS.length;

/* ==========================================================================================================
   SCORING RULES (version 1). Transparent on purpose: every rule is listed here and nowhere else.
   ----------------------------------------------------------------------------------------------------------
   Skin type: four running scores (dry, normal, combination, oily). Highest score wins.
     Q1 feel after cleansing (+3): tight or flaky -> dry | comfortable -> normal | shiny on forehead, nose and chin
                                   -> combination | shiny all over -> oily
     Q2 pores (+2):                hard to see -> dry | a little around the nose -> normal | nose, forehead and chin
                                   -> combination | most of the face -> oily
     Q5 climate (+1):              cold or dry -> dry | mild -> normal | changes with the seasons -> combination
                                   | warm and humid -> oily
     Tie: the Q1 answer wins (how skin feels is the strongest signal), then normal, combination, dry, oily.
   Sensitive flag (Q3 only): rarely or never -> not sensitive | now and then -> sensitive, shown as
     "a little sensitive" (sensitivity "some") | often -> sensitive (sensitivity "often").
   Concerns (Q4): the one or two picked, in the order shown. They weigh product words (PICK rules below) and
     count double in the Treat step.
   Routine length (Q6): 3 steps = Cleanse, Moisturize, Protect | 5 steps = all five phases |
     full ritual = five phases plus one weekly mask (Weekly extras).
   Sunscreen (Q7): "on sunny days" and "not yet" favour light sun serums, fluids and sticks in the Protect step.
   Offer collection: skin-type-sensitive when Q3 is "often", otherwise skin-type-<type>.
   ========================================================================================================== */
/** @type {Record<string, string>} */
const FEEL = { tight: 'dry', comfortable: 'normal', tzone: 'combination', shiny: 'oily' };
/** @type {Record<string, string>} */
const PORES = { small: 'dry', nose: 'normal', tzone: 'combination', most: 'oily' };
/** @type {Record<string, string>} */
const CLIMATE = { cold: 'dry', mild: 'normal', seasonal: 'combination', humid: 'oily' };
/** @type {Record<string, string>} */
const SENSITIVITY = { rarely: 'none', sometimes: 'some', often: 'often' };

/** @param {Record<string, any>} a */
const typeOf = (a) => {
  /** @type {Record<string, number>} */
  const s = { dry: 0, normal: 0, combination: 0, oily: 0 };
  const feel = FEEL[a.feel];
  const pores = PORES[a.pores];
  const climate = CLIMATE[a.climate];
  if (feel) s[feel] = (s[feel] ?? 0) + 3;
  if (pores) s[pores] = (s[pores] ?? 0) + 2;
  if (climate) s[climate] = (s[climate] ?? 0) + 1;
  const order = [feel, 'normal', 'combination', 'dry', 'oily'].filter(Boolean);
  return Object.keys(s).sort((x, y) => (s[y] ?? 0) - (s[x] ?? 0) || order.indexOf(x) - order.indexOf(y))[0] || 'normal';
};

/**
 * @typedef {{ type: string, sensitive: boolean, sensitivity: string, concerns: string[], length: string, climate: string, sun: string }} QuizResult
 * @param {Record<string, any>} [a]
 * @returns {QuizResult}
 */
const resultOf = (a = {}) => {
  const sensitivity = SENSITIVITY[a.react] || 'none';
  return {
    type: typeOf(a),
    sensitive: sensitivity !== 'none',
    sensitivity,
    concerns: (Array.isArray(a.goals) ? a.goals : []).filter((c) => CONCERN_KEYS.includes(c)).slice(0, 2),
    length: a.length || 'five',
    climate: a.climate || 'mild',
    sun: a.sun || 'daily',
  };
};
/** @param {QuizResult} r */
const offerKeyOf = (r) => (r.sensitivity === 'often' ? 'sensitive' : r.type);

/* ---------- Routine phases (5 numbered phases + unnumbered Weekly extras), same map as the prototype ---------- */
/** @type {Record<string, { key: string, n: number | null }>} */
const PHASES = {
  cleanse: { key: 'cleanse', n: 1 },
  tone: { key: 'tone', n: 2 },
  treat: { key: 'treat', n: 3 },
  moisturize: { key: 'moisturize', n: 4 },
  protect: { key: 'protect', n: 5 },
  extras: { key: 'extras', n: null },
};
/** @param {string} type */
const phaseOf = (type) => {
  const t = String(type || '').toLowerCase().trim();
  if (!t) return null;
  if (/\bset\b|tool|accessor/.test(t)) return null;
  if (/\bsun|spf/.test(t)) return PHASES.protect; /* before serum: "Sun Serum" and "Sun Cushion" protect */
  if (/lip|tint|blush|gloss|foundation|concealer|shadow|mascara|liner|brow|palette|primer|cushion/.test(t)) return null;
  if (/eye/.test(t)) return PHASES.treat; /* "Eye Patch" is eye care, "Eye Cream" is not a moisturizer */
  if (/facial oil|face oil/.test(t)) return PHASES.moisturize; /* a facial oil is the last layer, not a cleanser */
  if (/cleans|foam|balm|\boil\b/.test(t)) return PHASES.cleanse;
  if (/peel|exfoliat|scrub/.test(t)) return PHASES.extras; /* exfoliating pads before the toner-pad rule */
  if (/sleeping/.test(t)) return PHASES.moisturize; /* a sleeping mask is the last layer at night */
  if (/mask|patch|\bpack\b/.test(t)) return PHASES.extras;
  if (/pad|toner|mist/.test(t)) return PHASES.tone;
  if (/essence|serum|ampoule/.test(t)) return PHASES.treat;
  if (/cream|lotion|emulsion|moist|gel/.test(t)) return PHASES.moisturize;
  return null;
};

/* ==========================================================================================================
   PICK RULES (product heuristics, version 1). Only real store products from the section payload.
   ----------------------------------------------------------------------------------------------------------
   0. Pool: in-stock products of the five skin type collections (the section never writes sold-out products,
      and available === false is skipped again here).
   1. Step: phaseOf(product type) must equal the step (Cleanse, Tone, Treat, Moisturize, Protect, Weekly extras),
      with these step limits: Cleanse skips toners and pads; Treat skips eye care and patches; Moisturize skips eye
      creams, sleeping masks, lip care and men's all-in-ones; Protect skips cushions, tinted and tone-up sunscreens;
      Weekly extras keeps face masks only (no lip, eye or patch).
   2. Always skipped: sold out, products with several variants (so "Add all to bag" works in one tap), sets, kits,
      duos and refills.
   3. Words: each product gets TAGS from words in its title and type (table below). A tag scores by the shopper's
      skin type (W_TYPE), each picked concern (W_CONCERN, double in Treat), sensitivity (W_SENS), climate (W_CLIMATE)
      and, in Protect only, sunscreen habit (W_SUN). Score = sum of the weights of its tags.
   4. Sensitivity "often" removes products tagged AHA, exfoliation or retinol entirely.
   5. Curated edit: +1 when the product is in the shopper's offer collection (skin-type-<type> or
      skin-type-sensitive), +0.5 when it is only in the skin-type-<type> collection.
   6. Tie-breakers, each smaller than one keyword: +0.5 if it is in the Bestsellers collection, up to +0.4 for
      verified review count (reviews.rating_count), -1 if the routine already has that brand (variety).
      Then lower price, then handle (stable order).
   7. "Picked for" under each card names the two tags that added the most. No positive tag: "A bestseller in this
      step" when it is one, otherwise "Fits this step".
   ========================================================================================================== */
/** @type {Array<[string, RegExp]>} */
const TAGS = [
  ['bha', /\bbha\b|salicylic/i],
  ['aha', /\baha\b|glycolic|lactic|mandelic/i],
  ['exfoliating', /exfoliat|peel(?:ing)?\b|scrub/i],
  ['niacinamide', /niacinamide/i],
  ['hyaluronic', /hyalu/i],
  ['ceramide', /ceramide/i],
  ['centella', /centella|\bcica|madeca|tiger ?grass|\bteca\b/i],
  ['panthenol', /panthenol/i],
  ['heartleaf', /heartleaf|houttuynia/i],
  ['mugwort', /mugwort|artemisia/i],
  ['teatree', /tea ?tree/i],
  ['squalane', /squalane/i],
  ['vitc', /vitamin ?c\b|vita ?c\b|ascorb/i],
  ['rice', /\brice\b/i],
  ['retinol', /retinol|retinal\b|retinoid/i],
  ['bakuchiol', /bakuchiol/i],
  ['peptide', /peptide/i],
  ['pdrn', /pdrn/i],
  ['collagen', /collagen/i],
  ['propolis', /propolis/i],
  ['snail', /snail/i],
  ['glutathione', /glutathi/i],
  ['tranexamic', /tranexamic/i],
  ['arbutin', /arbutin/i],
  ['clay', /\bclay|\bmud\b/i],
  ['pore', /\bpores?\b|blackhead|whitehead/i],
  ['sebum', /sebum|oil[- ]?control|\bmatt/i],
  ['blemish', /blemish|\bacne|\bac\b|trouble|(?<!dark )\bspot\b|\bclear/i],
  ['barrier', /barrier/i],
  ['calming', /calm|sooth|relief/i],
  ['bright', /bright|tone[- ]?up|dark ?spot|radian|dull/i],
  ['firming', /firm|lift|wrinkle|elastic|adenosine|bounce/i],
  ['moisture', /moist|hydra|\baqua\b|water(?!proof)|\bdew/i],
  ['gel', /\bgel\b|jelly|sorbet/i],
  ['light', /\blight|fresh|watery|airy|fluid|sun ?serum|\bessence\b/i],
  ['rich', /\brich\b|intens|butter|\bbalm\b|nourish/i],
  ['mineral', /mineral|physical|zinc/i],
  ['stick', /\bstick\b/i],
];
/** @type {Record<string, Record<string, number>>} */
const W_TYPE = {
  dry: { hyaluronic: 2, ceramide: 3, squalane: 3, moisture: 2, barrier: 2, rich: 2, panthenol: 1, snail: 1, gel: -1, clay: -3, sebum: -3, pore: -1, bha: -1 },
  oily: { bha: 3, niacinamide: 2, pore: 2, sebum: 3, clay: 2, teatree: 2, gel: 2, light: 2, rich: -3, squalane: -1 },
  combination: { niacinamide: 2, gel: 2, light: 1, hyaluronic: 1, pore: 1, bha: 1, rich: -1, clay: -1 },
  normal: { hyaluronic: 1, rice: 1, niacinamide: 1, moisture: 1, peptide: 1 },
};
/** @type {Record<string, Record<string, number>>} */
const W_CONCERN = {
  hydration: { hyaluronic: 3, moisture: 2, ceramide: 2, squalane: 2, snail: 1 },
  acne: { blemish: 3, bha: 3, teatree: 3, heartleaf: 2, centella: 1, niacinamide: 1, propolis: 1 },
  pores: { pore: 3, bha: 3, niacinamide: 2, clay: 2, sebum: 2 },
  brightening: { niacinamide: 3, vitc: 3, bright: 3, rice: 2, glutathione: 2, tranexamic: 2, arbutin: 2 },
  firmness: { retinol: 3, bakuchiol: 3, peptide: 3, pdrn: 2, collagen: 2, firming: 2 },
  redness: { centella: 3, calming: 3, heartleaf: 2, panthenol: 2, mugwort: 2, barrier: 1 },
};
/** @type {Record<string, Record<string, number>>} */
const W_SENS = {
  none: {},
  /* retinol -6 cancels its doubled firmness weight in Treat, so bakuchiol and peptides win for skin that reacts now and then */
  some: { centella: 1, calming: 1, panthenol: 1, aha: -4, exfoliating: -3, retinol: -6, vitc: -1 },
  often: { centella: 2, calming: 2, panthenol: 2, barrier: 1, bha: -3, vitc: -2 },
};
const BLOCK_OFTEN = new Set(['aha', 'exfoliating', 'retinol']);
/** @type {Record<string, Record<string, number>>} */
const W_CLIMATE = { humid: { gel: 1, light: 1, rich: -1 }, cold: { rich: 1, ceramide: 1, squalane: 1, gel: -1 }, seasonal: {}, mild: {} };
/** @type {Record<string, Record<string, number>>} */
const W_SUN = { daily: {}, sometimes: { light: 2, stick: 1 }, notyet: { light: 2, stick: 2 } };
/** @type {Record<string, (text: string) => boolean>} */
const STEP_LIMIT = {
  cleanse: (t) => !/toner|\bpads?\b/i.test(t),
  tone: () => true,
  treat: (t) => !/\beye\b|patch/i.test(t),
  moisturize: (t) => !/\beye\b|sleeping|\bmen\b|\blip/i.test(t),
  protect: (t) => !/cushion|tone[- ]?up|tint/i.test(t),
  extras: (t) => /facial mask|mask pack|sheet|wash[- ]?off/i.test(t) && !/\blip|\beye\b|patch/i.test(t),
};
const SKIP = /\bset\b|\bkit\b|\bduo\b|refill/i;
/** @type {Record<string, string[]>} */
const STEPS = {
  three: ['cleanse', 'moisturize', 'protect'],
  five: ['cleanse', 'tone', 'treat', 'moisturize', 'protect'],
  full: ['cleanse', 'tone', 'treat', 'moisturize', 'protect', 'extras'],
};

/**
 * @param {QuizResult} r
 * @param {string} step
 */
const weightsFor = (r, step) => {
  /** @type {Record<string, number>} */
  const w = {};
  /** @param {Record<string, number> | undefined} o @param {number} [m] */
  const add = (o, m = 1) => {
    for (const [k, v] of Object.entries(o || {})) w[k] = (w[k] || 0) + v * m;
  };
  add(W_TYPE[r.type]);
  for (const c of r.concerns) add(W_CONCERN[c], step === 'treat' ? 2 : 1);
  add(W_SENS[r.sensitivity]);
  add(W_CLIMATE[r.climate]);
  if (step === 'protect') add(W_SUN[r.sun]);
  return w;
};

/* ---------- Card title helpers (fallbacks for custom.display_title and custom.net_content) ---------- */
/** @type {Record<string, string>} official brand casing where the vendor string differs */
const BRAND_NAME = { 'bio heal boh': 'BIOHEAL BOH', jumiso: 'JUMISO', 'manyo factory': 'manyo' };
const ACRONYM = /^(SPF|PA|PDRN|BHA|AHA|PHA|LHA|NMN|NAD|GSA|TTSS|UV|VT|EM|TX|AC|II|III)$/;
const SIZE_RE = /(\d+(?:\.\d+)?)\s?(ml|g|oz)\b(?:\s*[x×]\s*(\d+)\s?(?:ea|pcs|p)?\b)?/i;
/** @param {string} s */
const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** @param {string} s */
const calmCaps = (s) => {
  const letters = s.replace(/[^A-Za-z]/g, '');
  if (!letters || letters.replace(/[^A-Z]/g, '').length / letters.length < 0.8) return s;
  return s.replace(/[A-Za-z]+/g, (w) => (w.length <= 2 || ACRONYM.test(w) ? w : w[0] + w.slice(1).toLowerCase()));
};
/** @param {QuizProduct} p */
const brandOf = (p) => BRAND_NAME[String(p.v || '').trim().toLowerCase()] || String(p.v || '').trim();
/** @param {QuizProduct} p */
const titleParts = (p) => {
  const base = p.d
    ? String(p.d)
    : calmCaps(
        String(p.t || '')
          .replace(new RegExp(`^\\[?${reEsc(p.v || '')}\\]?\\s*`, 'i'), '')
          .replace(/\s*\(\d+\s*colou?rs?\)/i, '')
          .replace(/\s*\[[^\]]*\]\s*$/, '')
          .replace(/\s+korean\s+skin\s?care\s*$/i, '')
          .trim() || String(p.t || '')
      );
  const m = p.n > 1 ? null : String(p.t || '').match(SIZE_RE);
  if (!m) return { title: base, size: '' };
  const size = `${m[1]} ${String(m[2]).toLowerCase()}${m[3] ? ` x ${m[3]}` : ''}`;
  const title =
    base
      .replace(new RegExp(reEsc(m[0]), 'i'), ' ')
      .replace(/\s{2,}/g, ' ')
      .trim()
      .replace(/[\s,/-]+$/, '')
      .trim() || base;
  return { title, size };
};

const CARD_SIZES = '(min-width: 1200px) 18vw, (min-width: 700px) 30vw, 46vw';

/**
 * @typedef {object} QuizProduct
 * @property {string} h handle
 * @property {string} t title
 * @property {string | null} d custom.display_title
 * @property {string} v vendor
 * @property {string} y product type
 * @property {string[]} g tags
 * @property {boolean} a available
 * @property {number} n variant count
 * @property {number | null} id first available variant id
 * @property {number} p price (minor units)
 * @property {number} c compare-at price (minor units, 0 when none)
 * @property {string} pf formatted price
 * @property {string | null} cf formatted compare-at price (only when on sale)
 * @property {string} u url
 * @property {string | null} i image url
 * @property {string | null} s image srcset
 * @property {boolean} b in the bestsellers collection
 * @property {number} rc review count
 * @property {number | string} rv review rating
 * @property {string[]} [tags] derived routine tags
 */

/**
 * @typedef {object} QuizRefs
 * @property {HTMLElement} intro
 * @property {HTMLElement} introActions
 * @property {HTMLElement} question
 * @property {HTMLElement} result
 * @property {HTMLElement} status
 * @property {HTMLTemplateElement} icons
 * @property {HTMLScriptElement} data
 */

/** @extends {Component<QuizRefs>} */
class BadaSkinQuizComponent extends Component {
  requiredRefs = ['intro', 'introActions', 'question', 'result', 'status', 'icons', 'data'];

  /** @type {any} */
  #config = null;
  /** @type {any} */
  #copy = {};
  /** @type {QuizProduct[]} */
  #products = [];
  /** @type {Record<string, string>} */
  #icons = {};
  /** @type {{ v: number, step: number, answers: Record<string, any>, done: boolean, taken_at: string | null }} */
  #state = BadaSkinQuizComponent.#fresh();
  #lastPct = 0;
  /** @type {Array<{ step: string, p: QuizProduct, why: string }>} */
  #picks = [];
  /** @type {{ code: string, expires_at?: string, percent?: number } | null} */
  #offerCode = null;
  #profileSaved = false;
  /** The app saved the profile and answered without a code */
  #noCode = false;
  #busy = false;
  /** The app answered 401 (no signed-in customer behind the proxy): the next manual save goes through sign-in */
  #signInNeeded = false;
  /** @type {AbortController | null} */
  #saveController = null;
  /** Answer set number, bumped by restart(): a save answer that belongs to cleared answers is dropped */
  #run = 0;
  /** @type {Set<ReturnType<typeof setTimeout>>} */
  #timers = new Set();
  /** @type {Intl.PluralRules | null | undefined} undefined until first use, null when the page language has none */
  #pluralRules = undefined;

  static #fresh() {
    return { v: QUIZ_VERSION, step: 0, answers: {}, done: false, taken_at: null };
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#readConfig()) return;
    const saved = store.get(KEY_PROGRESS);
    this.#state =
      saved && saved.v === QUIZ_VERSION && saved.answers && typeof saved.answers === 'object'
        ? { ...BadaSkinQuizComponent.#fresh(), ...saved }
        : BadaSkinQuizComponent.#fresh();
    this.#boot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    this.#saveController?.abort();
    this.#saveController = null;
  }

  /* ---------- Setup ---------- */

  #readConfig() {
    try {
      this.#config = JSON.parse(this.refs.data.textContent || '{}');
    } catch (error) {
      console.error('[bada-skin-quiz] Invalid section payload', error);
      return false;
    }
    this.#copy = this.#config.copy || {};
    /** @type {QuizProduct[]} */
    const products = Array.isArray(this.#config.products) ? this.#config.products : [];
    this.#products = products.filter((p) => p && p.a !== false);
    for (const p of this.#products) {
      const text = `${p.t} ${p.y}`;
      p.tags = TAGS.filter(([, re]) => re.test(text)).map(([key]) => key);
    }
    this.#icons = {};
    for (const el of this.refs.icons.content.querySelectorAll('[data-icon]')) {
      if (el instanceof HTMLElement && el.dataset.icon) this.#icons[el.dataset.icon] = el.innerHTML.trim();
    }
    return true;
  }

  #boot() {
    const params = new URLSearchParams(window.location.search);
    /* Back from sign-in: ?save=1, or the note saveResult() left on this device (always read once, then cleared) */
    const intent = takeSaveIntent();
    const wantsSave = params.has('save') || intent;
    if (params.has('retake')) {
      this.#cleanUrl(['retake', 'save']);
      this.restart();
      return;
    }
    if (params.has('save')) this.#cleanUrl(['save']);
    if (this.#state.done) {
      this.#go(N + 1);
      /* Auto-save only for a signed-in shopper, and never sends anyone to sign in again (no loop) */
      if (wantsSave && this.#config.customer) this.#save(true);
      return;
    }
    /* Not #go(0): keep the saved step so the intro can offer to resume */
    this.#renderIntro();
    this.#show(this.refs.intro);
  }

  /** @param {string[]} names */
  #cleanUrl(names) {
    try {
      const url = new URL(window.location.href);
      for (const name of names) url.searchParams.delete(name);
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    } catch {
      /* sandboxed frame */
    }
  }

  #persist() {
    store.set(KEY_PROGRESS, this.#state);
  }

  /** @param {() => void} fn @param {number} ms */
  #later(fn, ms) {
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      fn();
    }, ms);
    this.#timers.add(timer);
    return timer;
  }

  /**
   * The one live region of the section. `html` is translated text (escaped) or text escaped by the caller.
   * @param {string} html
   */
  #say(html) {
    const { status } = this.refs;
    status.textContent = '';
    this.#later(() => {
      status.innerHTML = html;
    }, 60);
  }

  /** @param {string} name @param {string} [modifier] */
  #icon(name, modifier) {
    const svg = this.#icons[name] || '';
    return svg ? `<span class="bada-quiz-icon bada-quiz-icon--${modifier || name}" aria-hidden="true">${svg}</span>` : '';
  }

  /**
   * A count string in the page language. The section passes every plural form the locale file has (en and de: one,
   * other; ar: zero, one, two, few, many, other); Intl.PluralRules picks the form, other when it is missing.
   * @param {string} key - entry in copy.plurals, for example 'routine.count'
   * @param {number} count - fills {count}
   * @param {Record<string, unknown>} [values] - more plain text tokens
   * @param {Record<string, string>} [raw] - translated or trusted tokens
   */
  #plural(key, count, values = {}, raw = {}) {
    if (this.#pluralRules === undefined) {
      try {
        this.#pluralRules = new Intl.PluralRules(document.documentElement.lang || undefined);
      } catch {
        this.#pluralRules = null;
      }
    }
    /** @type {Record<string, string>} */
    const forms = this.#copy.plurals?.[key] || {};
    const form = this.#pluralRules?.select(count) || 'other';
    return fill(forms[form] ?? forms.other ?? '', { count, ...values }, raw);
  }

  get #uid() {
    return String(this.#config?.uid || `bada-quiz-${this.dataset.sectionId || ''}`);
  }

  /* ---------- Screens ---------- */

  /**
   * @param {HTMLElement} screen
   * @param {string} [focusSelector] - set for shopper-initiated changes: animates, scrolls and moves focus
   */
  #show(screen, focusSelector) {
    for (const s of [this.refs.intro, this.refs.question, this.refs.result]) s.hidden = s !== screen;
    if (!focusSelector) return; /* first paint never animates */
    screen.classList.remove('is-in');
    screen.classList.add('is-entering');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        screen.classList.add('is-in');
        screen.classList.remove('is-entering');
      })
    );
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
    const target = screen.querySelector(focusSelector);
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /**
   * @param {number} n - 0 intro, 1 to N questions, N + 1 result
   * @param {boolean} [focus]
   * @param {string} [announce] - replaces the "Question n of N" announcement (translated text)
   */
  #go(n, focus = false, announce = '') {
    this.#state.step = n;
    this.#persist();
    if (n <= 0) {
      this.#renderIntro();
      this.#show(this.refs.intro, focus ? '.bada-quiz-intro__title' : undefined);
      return;
    }
    if (n > N) {
      const r = this.#renderResult();
      this.#show(this.refs.result, focus ? '.bada-quiz-rhead__title' : undefined);
      if (focus) this.#say(fill(this.#copy.result?.announce, {}, { type: this.#typeLine(r) }));
      return;
    }
    this.#renderQuestion(n);
    this.#show(this.refs.question, focus ? '.bada-quiz-q__title' : undefined);
    if (focus) this.#say(announce || fill(this.#copy.question?.progress, { step: n, total: N }));
  }

  #renderIntro() {
    const t = this.#copy.intro || {};
    const s = this.#state;
    const inProgress = !s.done && s.step > 0 && Object.keys(s.answers).length > 0;
    const arrow = this.#icon('arrow', 'forward');
    const restart = `<button class="button button-secondary bada-quiz__button" type="button" on:click="/restart">${t.startOver}</button>`;
    if (s.done) {
      this.refs.introActions.innerHTML = `<button class="button bada-quiz__button" type="button" on:click="/showResult">${t.seeResult} ${arrow}</button>${restart}`;
    } else if (inProgress) {
      const at = Math.min(Math.max(1, s.step), N);
      this.refs.introActions.innerHTML = `<p class="bada-quiz-intro__resume">${fill(t.resume, { step: at, total: N })}</p><button class="button bada-quiz__button" type="button" on:click="/resume">${t.continue} ${arrow}</button>${restart}`;
    } else if (!this.refs.introActions.querySelector('[on\\:click="/start"]')) {
      this.refs.introActions.innerHTML = `<button class="button bada-quiz__button" type="button" on:click="/start">${t.start} ${arrow}</button>`;
    }
  }

  /* ---------- Question ---------- */

  /** @param {number} count */
  #countText(count) {
    const t = this.#copy.question || {};
    return count === 0 ? t.countNone : count === 1 ? t.countOne : t.countMax;
  }

  /**
   * @param {typeof QUESTIONS[number]} q
   * @param {string} value
   * @param {Set<string> | string | undefined} selected
   */
  #optionHTML(q, value, selected) {
    const multi = q.type === 'checkbox';
    const id = `${this.#uid}-q-${q.id}-${value}`;
    const [label = esc(value), desc = ''] = this.#copy.questions?.[q.id]?.options?.[value] || [];
    const on = selected instanceof Set ? selected.has(value) : selected === value;
    return `<label class="bada-quiz-opt${multi ? ' bada-quiz-opt--check' : ''}" for="${id}">
      <input class="bada-quiz-opt__input" id="${id}" type="${multi ? 'checkbox' : 'radio'}" name="${this.#uid}-${q.id}" value="${esc(value)}" aria-labelledby="${id}-l"${desc ? ` aria-describedby="${id}-d"` : ''}${on ? ' checked' : ''}>
      <span class="bada-quiz-opt__tick" aria-hidden="true">${this.#icons.check || ''}</span>
      <span class="bada-quiz-opt__text"><span class="bada-quiz-opt__label" id="${id}-l">${label}</span>${desc ? `<span class="bada-quiz-opt__desc" id="${id}-d">${desc}</span>` : ''}</span>
    </label>`;
  }

  /** @param {number} n */
  #renderQuestion(n) {
    const q = QUESTIONS[n - 1];
    if (!q) return;
    const t = this.#copy.question || {};
    const qc = this.#copy.questions?.[q.id] || {};
    const uid = this.#uid;
    const multi = q.type === 'checkbox';
    const value = this.#state.answers[q.id];
    const selected = multi ? new Set(Array.isArray(value) ? value : []) : value;
    const describedBy = `${uid}-q-help${multi ? ` ${uid}-q-count` : ''}`;
    this.refs.question.innerHTML = `
      <div class="bada-quiz-q">
        <div class="bada-quiz-progress">
          <p class="bada-quiz-progress__text">${fill(t.progress, { step: n, total: N })}</p>
          <div class="bada-quiz-progress__line" aria-hidden="true"><span class="bada-quiz-progress__fill" style="--bq-progress: ${this.#lastPct.toFixed(4)}"></span></div>
        </div>
        <form class="bada-quiz-form" novalidate on:submit="/next" on:change="/answer">
          <fieldset class="bada-quiz-field" aria-describedby="${describedBy}">
            <legend class="bada-quiz-legend"><h1 id="${uid}-q-title" class="bada-quiz-q__title" tabindex="-1">${qc.title || ''}</h1></legend>
            <p class="bada-quiz-q__help" id="${uid}-q-help">${qc.help || ''}</p>
            ${multi ? `<p class="bada-quiz-q__count" id="${uid}-q-count" aria-live="polite">${this.#countText(selected instanceof Set ? selected.size : 0)}</p>` : ''}
            <div class="bada-quiz-opts${q.options.length > 4 ? ' bada-quiz-opts--2' : ''}">${q.options.map((v) => this.#optionHTML(q, v, selected)).join('')}</div>
            <p class="bada-quiz-q__error" id="${uid}-q-err" role="alert" hidden></p>
          </fieldset>
          <div class="bada-quiz-nav">
            <button class="bada-quiz-back" type="button" on:click="/back">${this.#icon('arrow', 'back')} ${t.back}</button>
            <button class="button bada-quiz__button bada-quiz-next" type="submit">${n === N ? t.finish : t.next} ${this.#icon('arrow', 'forward')}</button>
          </div>
        </form>
      </div>`;
    const pct = n / N;
    const bar = this.refs.question.querySelector('.bada-quiz-progress__fill');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (bar instanceof HTMLElement) bar.style.setProperty('--bq-progress', pct.toFixed(4));
      })
    );
    this.#lastPct = pct;
    if (multi) this.#syncLimit(q);
  }

  /** @param {typeof QUESTIONS[number]} q */
  #syncLimit(q) {
    const boxes = [...this.refs.question.querySelectorAll('input[type="checkbox"]')].filter(
      (b) => b instanceof HTMLInputElement
    );
    const count = boxes.filter((b) => /** @type {HTMLInputElement} */ (b).checked).length;
    for (const box of boxes) {
      const input = /** @type {HTMLInputElement} */ (box);
      input.disabled = !input.checked && count >= (q.max || 2);
      input.closest('.bada-quiz-opt')?.classList.toggle('is-off', input.disabled);
    }
    const counter = this.refs.question.querySelector('.bada-quiz-q__count');
    if (counter) counter.innerHTML = this.#countText(count);
  }

  /** @param {typeof QUESTIONS[number]} q */
  #readAnswer(q) {
    const checked = [...this.refs.question.querySelectorAll('.bada-quiz-opt__input:checked')].map(
      (input) => /** @type {HTMLInputElement} */ (input).value
    );
    if (q.type === 'checkbox') return checked.slice(0, q.max || 2);
    return checked[0] || null;
  }

  /** @param {typeof QUESTIONS[number]} q */
  #showError(q) {
    const t = this.#copy.question || {};
    const multi = q.type === 'checkbox';
    const error = this.refs.question.querySelector('.bada-quiz-q__error');
    const field = this.refs.question.querySelector('.bada-quiz-field');
    if (!(error instanceof HTMLElement) || !field) return;
    /* Show the alert region first, then fill it, so screen readers announce the words */
    error.hidden = false;
    error.textContent = '';
    this.#later(() => {
      error.innerHTML = `${this.#icon('warning')} ${multi ? t.errorMulti : t.errorSingle}`;
    }, 30);
    field.setAttribute('aria-describedby', `${this.#uid}-q-help${multi ? ` ${this.#uid}-q-count` : ''} ${this.#uid}-q-err`);
    const first = this.refs.question.querySelector('.bada-quiz-opt__input:not(:disabled)');
    if (first instanceof HTMLElement) first.focus();
  }

  #hideError() {
    const q = QUESTIONS[this.#state.step - 1];
    const error = this.refs.question.querySelector('.bada-quiz-q__error');
    if (!q || !(error instanceof HTMLElement) || error.hidden) return;
    error.hidden = true;
    this.refs.question
      .querySelector('.bada-quiz-field')
      ?.setAttribute('aria-describedby', `${this.#uid}-q-help${q.type === 'checkbox' ? ` ${this.#uid}-q-count` : ''}`);
  }

  /* ---------- Result ---------- */

  /** @param {QuizResult} r */
  #typeLine(r) {
    const c = this.#copy;
    const label = c.types?.[r.type] || '';
    if (r.sensitivity !== 'some' && r.sensitivity !== 'often') return label;
    return fill(c.sensitivity?.pattern, {}, { type: label, sensitivity: c.sensitivity?.[r.sensitivity] || '' });
  }

  /** Same words as #typeLine, with the sensitivity kept on one line in the headline. @param {QuizResult} r */
  #typeLineHTML(r) {
    const c = this.#copy;
    const label = c.types?.[r.type] || '';
    if (r.sensitivity !== 'some' && r.sensitivity !== 'often') return label;
    return fill(
      c.sensitivity?.pattern,
      {},
      { type: label, sensitivity: `<span class="bada-quiz-nowrap">${c.sensitivity?.[r.sensitivity] || ''}</span>` }
    );
  }

  /** @param {string} key */
  #members(key) {
    const items = this.#config.collections?.[key]?.items;
    return new Set(Array.isArray(items) ? items : []);
  }

  /** @param {QuizResult} r */
  #routineOf(r) {
    const offerSet = this.#members(offerKeyOf(r));
    const typeSet = this.#members(r.type);
    const used = new Set();
    /** @type {Array<{ step: string, p: QuizProduct, why: string }>} */
    const picks = [];
    const t = this.#copy.routine || {};
    const say = this.#copy.reasons || {};
    for (const step of STEPS[r.length] || STEPS.five || []) {
      const w = weightsFor(r, step);
      const limit = STEP_LIMIT[step] || (() => true);
      const pool = this.#products.filter((p) => {
        if (p.a === false || p.n > 1 || !p.id || SKIP.test(p.t)) return false;
        if (phaseOf(p.y)?.key !== step) return false;
        if (!limit(`${p.t} ${p.y}`)) return false;
        if (r.sensitivity === 'often' && (p.tags || []).some((tag) => BLOCK_OFTEN.has(tag))) return false;
        return true;
      });
      const scored = pool
        .map((p) => {
          /** @type {Array<[string, number]>} */
          const contrib = (p.tags || []).map((tag) => [tag, w[tag] || 0]);
          let score = contrib.reduce((sum, [, v]) => sum + v, 0);
          if (offerSet.has(p.h)) score += 1;
          else if (typeSet.has(p.h)) score += 0.5;
          if (p.b) score += 0.5;
          if (p.rc > 0) score += Math.min(0.4, p.rc / 500);
          if (used.has(p.v)) score -= 1;
          const why = contrib
            .filter(([, v]) => v > 0)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([tag]) => say[tag] || esc(tag));
          return { p, score, why };
        })
        .sort((a, b) => b.score - a.score || a.p.p - b.p.p || a.p.h.localeCompare(b.p.h));
      const best = scored[0];
      if (!best) continue;
      used.add(best.p.v);
      let why = best.p.b ? t.whyBestseller : t.whyFit;
      if (best.why.length === 2) why = fill(t.whyTwo, {}, { first: best.why[0] || '', second: best.why[1] || '' });
      else if (best.why.length === 1) why = fill(t.whyOne, {}, { first: best.why[0] || '' });
      picks.push({ step, p: best.p, why });
    }
    return picks;
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return formatMoney(cents, this.#config.moneyFormat || '${{amount}}', this.#config.currency || 'USD');
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** @param {string | undefined} iso */
  #date(iso) {
    const d = new Date(iso || '');
    if (Number.isNaN(+d)) return '';
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(d);
    } catch {
      return d.toDateString();
    }
  }

  /** @param {QuizProduct} p */
  #cardHTML(p) {
    const c = this.#copy.card || {};
    const brand = brandOf(p);
    const { title, size } = titleParts(p);
    const name = `${brand} ${title}`.trim();
    const off = p.c > p.p ? Math.round((1 - p.p / p.c) * 100) : 0;
    /* One badge at most: Wave 8 (tag wave-live, the owner's badge artwork) > Sale. The struck price still shows a sale. */
    const wave = Array.isArray(p.g) && p.g.includes('wave-live') && this.#config.waveBadge;
    let badge = '';
    if (wave) badge = `<span class="bada-quiz-card__badge bada-quiz-card__badge--wave"><img src="${esc(this.#config.waveBadge)}" alt="" width="63" height="24" loading="lazy"></span>`;
    else if (off > 0) badge = `<span class="bada-quiz-card__badge">${fill(c.save, { percent: off })}</span>`;
    const image = p.i
      ? `<img src="${esc(p.i)}"${p.s ? ` srcset="${esc(p.s)}" sizes="${CARD_SIZES}"` : ''} alt="" loading="lazy" width="500" height="500">`
      : '';
    const ratingValue = Number(p.rv) || 0;
    const ratingCount = Number(p.rc) || 0;
    /* Ratings only when real review data exists */
    const rating =
      ratingCount > 0 && ratingValue > 0
        ? `<div class="bada-quiz-card__rating"><span class="bada-quiz-stars" style="--bq-stars: ${Math.round((ratingValue / 5) * 100)}%" aria-hidden="true"></span><span aria-hidden="true">${ratingValue.toFixed(1)} (${ratingCount})</span><span class="visually-hidden">${this.#plural('card.rating_label', ratingCount, { rating: ratingValue.toFixed(1) })}</span></div>`
        : '';
    const price = p.cf
      ? `<span class="bada-quiz-price__now bada-quiz-price__now--sale"><span class="visually-hidden">${c.priceSale} </span>${esc(p.pf)}</span> <s class="bada-quiz-price__was"><span class="visually-hidden">${c.priceRegular} </span>${esc(p.cf)}</s>`
      : `<span class="bada-quiz-price__now">${esc(p.pf)}</span>`;
    return `<article class="bada-quiz-card">
      <div class="bada-quiz-card__frame">
        <a class="bada-quiz-card__media" href="${esc(p.u)}" tabindex="-1" aria-hidden="true">${badge}${image}</a>
        <button class="bada-quiz-card__add" type="button" on:click="/addOne" data-variant-id="${esc(p.id)}" data-name="${esc(name)}" aria-label="${fill(this.#copy.routine?.addOne, { product: name })}">${this.#icon('plus', 'add')}</button>
      </div>
      <div class="bada-quiz-card__info">
        <span class="bada-quiz-card__brand" aria-hidden="true"><bdi>${esc(brand)}</bdi></span>
        <h3 class="bada-quiz-card__heading"><a class="bada-quiz-card__title" href="${esc(p.u)}"><span class="visually-hidden">${esc(brand)} </span><bdi>${esc(title)}</bdi></a></h3>
        ${size ? `<p class="bada-quiz-card__meta"><bdi dir="ltr">${esc(size)}</bdi></p>` : ''}
        ${rating}
        <div class="bada-quiz-price">${wave ? `<span class="visually-hidden">${c.wave} </span>` : ''}${price}</div>
      </div>
    </article>`;
  }

  /**
   * Restores what the app already stored for this customer (rendered by the section from metafields).
   * @param {QuizResult} r
   * @param {string} collection
   */
  #restoreSaved(r, collection) {
    const offer = this.#config.offer || {};
    const saved = offer.saved;
    if (
      saved &&
      typeof saved === 'object' &&
      saved.code &&
      !saved.redeemed_at &&
      new Date(saved.expires_at) > new Date() &&
      (!saved.collection || saved.collection === collection)
    ) {
      this.#offerCode = { code: String(saved.code), expires_at: saved.expires_at, percent: Number(saved.percent) || undefined };
    }
    const profile = offer.profile;
    if (profile && typeof profile === 'object') {
      const same =
        profile.type === r.type &&
        profile.sensitivity === r.sensitivity &&
        JSON.stringify(profile.concerns || []) === JSON.stringify(r.concerns);
      if (same) this.#profileSaved = true;
    }
  }

  #renderResult() {
    const r = resultOf(this.#state.answers);
    if (!this.#state.taken_at) {
      this.#state.taken_at = new Date().toISOString();
      this.#persist();
    }
    const c = this.#copy;
    const uid = this.#uid;
    const key = offerKeyOf(r);
    const coll = this.#config.collections?.[key] || {};
    this.#offerCode = null;
    this.#profileSaved = false;
    this.#noCode = false;
    this.#restoreSaved(r, coll.handle || `skin-type-${key}`);

    const picks = this.#routineOf(r);
    this.#picks = picks;
    const total = picks.reduce((sum, x) => sum + (Number(x.p.p) || 0), 0);
    const numbered = picks.filter((x) => PHASES[x.step]?.n).length;
    const hasExtra = picks.some((x) => !PHASES[x.step]?.n);
    const routine = c.routine || {};
    const concerns = r.concerns.map((k) => `<li class="bada-quiz-chip">${c.concerns?.[k] || ''}</li>`).join('');
    const often = r.sensitivity === 'often';
    const offerShown = Boolean(this.#config.offer?.show);

    const stepsHTML = picks
      .map((x) => {
        const phase = PHASES[x.step];
        const label = phase?.n
          ? `<span class="bada-quiz-step__n" aria-hidden="true">${phase.n}</span><span class="visually-hidden">${fill(routine.step, { number: phase.n })} </span>`
          : `<span class="bada-quiz-step__n bada-quiz-step__n--extra" aria-hidden="true">${this.#icons.plus || ''}</span><span class="visually-hidden">${routine.weekly} </span>`;
        return `<li class="bada-quiz-step${phase?.n ? '' : ' bada-quiz-step--extra'}">
            <p class="bada-quiz-step__label">${label}${c.phases?.[x.step] || ''}</p>
            ${this.#cardHTML(x.p)}
            <p class="bada-quiz-step__why">${this.#icon('info', 'why')} <span>${x.why}</span></p>
          </li>`;
      })
      .join('');

    /* No routine (no in-stock product fits any step, for example while the skin type collections are unpublished):
       a clear message and the way to the whole skincare range, never an empty grid */
    const routineHTML = picks.length
      ? `<div class="bada-quiz-routine__head">
          <div class="bada-quiz-routine__text">
            <h2 id="${uid}-routine-title" class="bada-quiz-routine__title">${this.#plural(hasExtra ? 'routine.title_steps_extra' : 'routine.title_steps', numbered)}</h2>
            <p class="bada-quiz-routine__intro">${routine.intro}</p>
          </div>
          <div class="bada-quiz-routine__buy">
            <p class="bada-quiz-routine__total"><span>${this.#plural('routine.count', picks.length)}</span> <b>${esc(this.#money(total))}</b></p>
            <button class="button bada-quiz__button" type="button" on:click="/addAll">${this.#icon('bag')} <span data-add-all-label>${routine.addAll}</span></button>
          </div>
        </div>
        <ol class="bada-quiz-steps bada-quiz-steps--${picks.length}" role="list">${stepsHTML}</ol>
        <details class="bada-quiz-how">
          <summary><span>${routine.howTitle}</span> ${this.#icon('caret')}</summary>
          <div class="bada-quiz-how__body">
            <p>${routine.howTypes}</p>
            <p>${routine.howWeights}${often ? ` ${routine.howOften}` : ''} ${routine.howSkips}</p>
            <p>${routine.howStart}</p>
          </div>
        </details>`
      : `<div class="bada-quiz-routine__empty">
          <p class="bada-quiz-routine__empty-text">${this.#icon('info', 'empty')} <span>${routine.empty}</span></p>
          <a class="button bada-quiz__button" href="${esc(this.#config.shopUrl || '/collections')}">${routine.emptyLink} ${this.#icon('arrow', 'forward')}</a>
        </div>`;

    this.refs.result.innerHTML = `
      <section class="bada-quiz-rhead" aria-labelledby="${uid}-r-title">
        <div class="bada-quiz-rhead__grid${offerShown ? '' : ' bada-quiz-rhead__grid--solo'}">
          <div class="bada-quiz-rhead__copy">
            <h1 id="${uid}-r-title" class="bada-quiz-rhead__title" tabindex="-1">${c.result?.title} <strong>${this.#typeLineHTML(r)}</strong></h1>
            <p class="bada-quiz-rhead__about">${c.about?.[r.type] || ''} ${c.patch?.[r.sensitivity] || ''}</p>
            ${concerns ? `<div class="bada-quiz-rhead__concerns"><p class="bada-quiz-rhead__label" id="${uid}-r-goals">${c.result?.concernsLabel}</p><ul class="bada-quiz-chips" role="list" aria-labelledby="${uid}-r-goals">${concerns}</ul></div>` : ''}
            <p class="bada-quiz-rhead__fine">${c.result?.finePrint}</p>
            <div class="bada-quiz-rhead__actions">
              <button class="button button-secondary bada-quiz__button" type="button" on:click="/restart">${c.result?.retake}</button>
            </div>
          </div>
          ${offerShown ? this.#offerHTML(r) : ''}
        </div>
      </section>
      <section class="bada-quiz-routine"${picks.length ? ` aria-labelledby="${uid}-routine-title"` : ''}>${routineHTML}</section>`;
    return r;
  }

  /** @param {QuizResult} r */
  #offerHTML(r) {
    const c = this.#copy;
    const uid = this.#uid;
    const key = offerKeyOf(r);
    const coll = this.#config.collections?.[key] || {};
    const label = c.types?.[key] || '';
    const percent = this.#offerCode?.percent || this.#config.offer?.percent || 10;
    const days = this.#config.offer?.days || 7;
    return `<aside class="bada-quiz-offer" aria-labelledby="${uid}-offer-title">
      <div class="bada-quiz-caustics" aria-hidden="true"></div>
      <p class="bada-quiz-offer__tag">${c.offer?.tag}</p>
      <h2 id="${uid}-offer-title" class="bada-quiz-offer__title">${fill(c.offer?.title, {}, { type: label })}</h2>
      <p class="bada-quiz-offer__deal"><span class="bada-quiz-offer__pct">${fill(c.offer?.percent, { percent })}</span> <span>${this.#plural('offer.deal_days', days, {}, { type: label })}</span></p>
      <div class="bada-quiz-offer__body">${this.#offerBodyHTML(r)}</div>
      ${coll.url ? `<a class="bada-quiz-link bada-quiz-offer__shop" href="${esc(coll.url)}"><span>${fill(c.offer?.shop, {}, { type: label })}</span> ${this.#icon('arrow', 'forward')}</a>` : ''}
    </aside>`;
  }

  /**
   * The part of the offer card that changes after saving. "Retake the quiz" is not part of it: it sits in the result
   * head, so it shows with or without the card.
   * @param {QuizResult} r
   */
  #offerBodyHTML(r) {
    const t = this.#copy.offer || {};
    const uid = this.#uid;
    const label = this.#copy.types?.[offerKeyOf(r)] || '';
    /* Theme editor only: the Bada app has the quiz offer off, so shoppers never see this card */
    if (this.#config.offer?.appOff) {
      return `<div class="bada-quiz-offer__save"><p class="bada-quiz-offer__note bada-quiz-offer__note--editor">${t.editorOff}</p></div>`;
    }
    let html = '';
    if (this.#offerCode) {
      const code = this.#offerCode.code;
      html += `<div class="bada-quiz-code">
          <span class="bada-quiz-code__label" id="${uid}-code-label">${t.codeLabel}</span>
          <span class="bada-quiz-code__value" id="${uid}-code" dir="ltr" aria-labelledby="${uid}-code-label ${uid}-code">${esc(code)}</span>
          <button class="bada-quiz-code__copy" type="button" on:click="/copyCode" data-code="${esc(code)}" aria-label="${fill(t.copyLabel, { code })}">${this.#icon('copy')} <span data-copy-label>${t.copy}</span></button>
        </div>`;
      const ends = this.#date(this.#offerCode.expires_at);
      if (ends) html += `<p class="bada-quiz-offer__ends">${this.#icon('calendar')} ${fill(t.ends, { date: ends })}</p>`;
      html += `<p class="bada-quiz-offer__fine">${fill(t.fine, {}, { type: label })}</p>`;
    }
    const proxy = this.#proxyUrl();
    if (this.#profileSaved) {
      html += `<div class="bada-quiz-offer__save"><p class="bada-quiz-offer__keep">${this.#noCode ? t.savedNoCode : t.saved}</p></div>`;
    } else {
      const hint = proxy && !this.#config.customer ? `<p class="bada-quiz-offer__note">${t.signInHint}</p>` : '';
      const editor = this.#config.designMode && !proxy ? `<p class="bada-quiz-offer__note bada-quiz-offer__note--editor">${t.editorNote}</p>` : '';
      html += `<div class="bada-quiz-offer__save">
          ${this.#offerCode ? '' : `<p class="bada-quiz-offer__keep">${t.savePrompt}</p>`}
          <div class="bada-quiz-offer__btns">
            <button class="button bada-quiz__button" type="button" on:click="/saveResult"><span data-save-label>${t.save}</span></button>
          </div>
          ${hint}${editor}
          <p class="bada-quiz-offer__message" data-offer-message hidden></p>
        </div>`;
    }
    return html;
  }

  #refreshOfferBody() {
    const body = this.refs.result.querySelector('.bada-quiz-offer__body');
    if (body) body.innerHTML = this.#offerBodyHTML(resultOf(this.#state.answers));
  }

  /** @param {string} html */
  #offerMessage(html) {
    const message = this.refs.result.querySelector('[data-offer-message]');
    if (message instanceof HTMLElement) {
      message.innerHTML = html;
      message.hidden = !html;
    }
    if (html) this.#say(html);
  }

  /** The app proxy path from the section, same origin only. */
  #proxyUrl() {
    const raw = String(this.#config.offer?.proxy || '').trim();
    if (!raw.startsWith('/') || raw.startsWith('//')) return '';
    try {
      const url = new URL(raw, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search : '';
    } catch {
      return '';
    }
  }

  /* ---------- Public handlers (on:click / on:submit / on:change in the rendered markup) ---------- */

  start() {
    this.#lastPct = 0;
    this.#go(1, true);
  }

  resume() {
    const at = Math.min(Math.max(1, this.#state.step), N);
    this.#lastPct = Math.max(0, (at - 1) / N);
    this.#go(at, true);
  }

  showResult() {
    this.#go(N + 1, true);
  }

  /**
   * "Retake the quiz" (result screen), "Start over" (intro) and ?retake: clears the saved answers on this device and
   * opens question 1, focus on its heading, with one announcement that the answers were cleared.
   */
  restart() {
    /* A save still on its way (proxy or app embed) belongs to the old answers */
    this.#run += 1;
    this.#saveController?.abort();
    this.#saveController = null;
    this.#state = BadaSkinQuizComponent.#fresh();
    this.#lastPct = 0;
    store.remove(KEY_PROGRESS);
    this.#go(1, true, fill(this.#copy.question?.restarted, { step: 1, total: N }));
  }

  back() {
    this.#lastPct = this.#state.step / N;
    this.#go(this.#state.step - 1, true);
  }

  /** @param {Event} event */
  answer(event) {
    const q = QUESTIONS[this.#state.step - 1];
    if (!q) return;
    const input = event.composedPath?.()[0];
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('bada-quiz-opt__input')) return;
    if (q.type === 'checkbox') this.#syncLimit(q);
    this.#state.answers[q.id] = this.#readAnswer(q);
    this.#persist();
    this.#hideError();
  }

  /** @param {SubmitEvent} event */
  next(event) {
    event.preventDefault();
    const q = QUESTIONS[this.#state.step - 1];
    if (!q) return;
    const value = this.#readAnswer(q);
    if (!value || (Array.isArray(value) && !value.length)) {
      this.#showError(q);
      return;
    }
    this.#state.answers[q.id] = value;
    if (this.#state.step === N) {
      this.#state.done = true;
      this.#state.taken_at = new Date().toISOString();
    }
    this.#go(this.#state.step + 1, true);
  }

  /** @param {MouseEvent} event */
  async copyCode(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const code = button.dataset.code || '';
    const t = this.#copy.offer || {};
    const label = button.querySelector('[data-copy-label]');
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch {
      /* blocked: select the text instead */
      const value = this.refs.result.querySelector('.bada-quiz-code__value');
      const selection = window.getSelection();
      if (value && selection) {
        const range = document.createRange();
        range.selectNodeContents(value);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    if (label) label.innerHTML = ok ? t.copied : t.selected;
    button.classList.toggle('is-done', ok);
    this.#say(fill(ok ? t.copiedStatus : t.selectedStatus, { code }));
    this.#later(() => {
      if (label) label.innerHTML = t.copy;
      button.classList.remove('is-done');
    }, 2400);
  }

  /** @param {MouseEvent} event */
  addOne(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || !button.dataset.variantId) return;
    this.#addToBag([button.dataset.variantId], button, button.dataset.name || '');
  }

  /** @param {MouseEvent} event */
  addAll(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const ids = this.#picks.map((x) => x.p.id).filter((id) => id != null).map(String);
    if (ids.length) this.#addToBag(ids, button, null);
  }

  /**
   * Adds variants with the same events and payloads as the product form (blocks/add-to-cart), so the
   * bag icon, the bag drawer (auto-open setting) and cart sections update as usual.
   * @param {string[]} variantIds
   * @param {HTMLButtonElement} button
   * @param {string | null} name - product name for a single add, null for "Add all"
   */
  async #addToBag(variantIds, button, name) {
    if (this.#busy) return;
    this.#busy = true;
    const t = this.#copy.routine || {};
    const single = name !== null;
    const label = button.querySelector('[data-add-all-label]');
    button.setAttribute('aria-disabled', 'true');
    if (label && !single) label.innerHTML = t.adding;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: variantIds.map((id) => ({ merchandiseId: id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    let ok = false;
    try {
      const response = await fetch(themeRoutes().cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();
      const cart = await refreshCart();
      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to bag failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bada-skin-quiz', sourceId: this.dataset.sectionId, itemCount: variantIds.length },
        });
        this.#say(data.description ? esc(data.description) : t.addError);
      } else {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'bada-skin-quiz',
            sourceId: this.dataset.sectionId,
            itemCount: variantIds.length,
            sections: data.sections,
            didError: false,
          },
        });
        ok = true;
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#say(t.addError);
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-disabled');
    }

    if (single) {
      if (!ok) return;
      const icon = button.querySelector('.bada-quiz-icon');
      button.classList.add('is-added');
      if (icon) icon.innerHTML = this.#icons.check || '';
      this.#say(fill(t.addedOneStatus, { product: name }));
      this.#later(() => {
        button.classList.remove('is-added');
        if (icon) icon.innerHTML = this.#icons.plus || '';
      }, 2000);
      return;
    }
    if (label) label.innerHTML = ok ? t.added : t.addAll;
    if (!ok) return;
    this.#say(this.#plural('routine.added_count', variantIds.length));
    const icon = button.querySelector('.bada-quiz-icon');
    if (icon) icon.innerHTML = this.#icons.check || '';
    this.#later(() => {
      if (label) label.innerHTML = t.addAll;
      if (icon) icon.innerHTML = this.#icons.bag || '';
    }, 2400);
  }

  /**
   * "Save my result" button. See the file header for the hook and the app proxy contract.
   */
  saveResult() {
    this.#save(false);
  }

  /** The skin type collection handle the offer card advertises. @param {QuizResult} r */
  #offerCollection(r) {
    const key = offerKeyOf(r);
    return this.#config.collections?.[key]?.handle || `skin-type-${key}`;
  }

  /**
   * Sign-in URL that brings the shopper back to this page with ?save=1. New customer accounts:
   * routes.storefront_login_url is /customer_authentication/login?return_to=<page> and return_to takes a relative
   * URL only (shopify.dev/docs/storefronts/themes/sign-in); any other login route gets the classic return_url.
   */
  #signInUrl() {
    const raw = String(this.#config.loginUrl || '').trim();
    if (!raw) return '';
    const back = `${window.location.pathname}?save=1`;
    try {
      const url = new URL(raw, window.location.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) return '';
      url.searchParams.set(url.pathname.endsWith('/customer_authentication/login') ? 'return_to' : 'return_url', back);
      return url.origin === window.location.origin ? url.pathname + url.search : url.href;
    } catch {
      return '';
    }
  }

  /**
   * Saves through the hook or the app proxy.
   * @param {boolean} auto true when it runs on its own after the sign-in round trip (never redirects again)
   */
  async #save(auto) {
    if (!this.#state.done || this.#busy) return;
    if (!this.#config.offer?.show || this.#config.offer?.appOff) return;
    const t = this.#copy.offer || {};
    const r = resultOf(this.#state.answers);
    const profile = { type: r.type, sensitivity: r.sensitivity, concerns: r.concerns.slice(), quiz_version: QUIZ_VERSION };
    const collection = this.#offerCollection(r);
    /* Answers to this save count only while these answers are still the shopper's (no retake in between) */
    const run = this.#run;

    /* 1. The Bada app embed can take over */
    const hook = new CustomEvent(SAVE_EVENT, {
      bubbles: true,
      cancelable: true,
      detail: {
        profile,
        collection,
        percent: this.#config.offer?.percent,
        days: this.#config.offer?.days,
        taken_at: this.#state.taken_at,
        /** @param {any} result */
        respond: (result) => {
          if (run === this.#run) this.applySaveResult(result);
        },
      },
    });
    this.dispatchEvent(hook);
    if (hook.defaultPrevented) return;

    /* 2. Built-in app proxy call */
    const proxy = this.#proxyUrl();
    if (!proxy) {
      this.#offerMessage(this.#config.designMode ? t.editorNote : t.unavailable);
      return;
    }
    if (!this.#config.customer || this.#signInNeeded) {
      /* Automatic saves never redirect, so a stale session cannot cause a sign-in loop */
      if (auto) return;
      const login = this.#signInUrl();
      if (!login) {
        this.#offerMessage(t.unavailable);
        return;
      }
      store.set(KEY_SAVE_INTENT, { at: Date.now() });
      window.location.assign(login);
      return;
    }

    this.#busy = true;
    const button = this.refs.result.querySelector('[on\\:click="/saveResult"]');
    const label = button?.querySelector('[data-save-label]');
    button?.setAttribute('aria-disabled', 'true');
    if (label) label.innerHTML = t.saving;
    this.#saveController?.abort();
    const controller = new AbortController();
    this.#saveController = controller;
    /** @type {'ok' | 'unavailable' | 'sign_in' | 'error' | 'aborted'} */
    let outcome = 'error';
    /** @type {any} */
    let data = null;
    try {
      const response = await fetch(proxy, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(profile),
        credentials: 'same-origin',
        signal: controller.signal,
      });
      const json = (response.headers.get('Content-Type') || '').includes('json');
      if (response.status === 404 || response.status === 501 || (response.ok && !json)) {
        /* The app is not installed or not connected yet (Shopify answers the proxy path with its 404 page) */
        outcome = 'unavailable';
      } else if (response.status === 401) {
        outcome = 'sign_in';
      } else if (response.ok) {
        data = await response.json();
        outcome = 'ok';
      } else {
        console.warn('[bada-skin-quiz] Save failed', response.status);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') outcome = 'aborted';
      else console.warn('[bada-skin-quiz] Save failed', error);
    } finally {
      if (this.#saveController === controller) this.#saveController = null;
    }
    this.#busy = false;
    if (outcome === 'aborted' || run !== this.#run) return;
    if (outcome === 'ok') {
      this.applySaveResult(data);
      return;
    }
    button?.removeAttribute('aria-disabled');
    if (label) label.innerHTML = t.save;
    if (outcome === 'unavailable') this.#offerMessage(t.unavailable);
    else if (outcome === 'sign_in') {
      this.#signInNeeded = true;
      this.#offerMessage(t.signInAgain);
    } else this.#offerMessage(t.error);
  }

  /** Puts the save button back after an answer that did not save. */
  #resetSaveButton() {
    const button = this.refs.result?.querySelector('[on\\:click="/saveResult"]');
    button?.removeAttribute('aria-disabled');
    const label = button?.querySelector('[data-save-label]');
    if (label) label.innerHTML = this.#copy.offer?.save || '';
  }

  /**
   * Public: applies the Bada app's answer to "Save my result" (also used by the app embed through the hook).
   * @param {{ saved?: boolean, offer?: { code?: string, collection?: string, percent?: number, starts_at?: string, expires_at?: string, redeemed_at?: string | null } | null }} result
   */
  applySaveResult(result) {
    /* An answer that arrives after "Retake the quiz" belongs to the answers that were cleared */
    if (!this.#state.done) return;
    const t = this.#copy.offer || {};
    if (!result || typeof result !== 'object') {
      this.#resetSaveButton();
      this.#offerMessage(t.error);
      return;
    }
    const r = resultOf(this.#state.answers);
    const offer = result.offer && typeof result.offer === 'object' ? result.offer : null;
    /* A code is shown only for the collection this card advertises ("One use on the <type> skin edit") */
    const fits = Boolean(offer?.code) && (!offer?.collection || offer.collection === this.#offerCollection(r));
    if (offer?.code && !fits) console.warn('[bada-skin-quiz] Code is for another collection', offer.collection);
    if (fits && offer) {
      this.#offerCode = { code: String(offer.code), expires_at: offer.expires_at, percent: Number(offer.percent) || undefined };
      store.set(KEY_OFFER, { ...offer, owner: this.#config.owner ?? null });
    }
    /* Saved without any code (the app found no offer for this shopper right now): say so, never imply a code */
    this.#noCode = Boolean(result.saved) && !offer?.code && !this.#offerCode;
    if (result.saved) {
      this.#profileSaved = true;
      store.set(KEY_PROFILE, {
        type: r.type,
        sensitive: r.sensitive,
        sensitivity: r.sensitivity,
        concerns: r.concerns.slice(),
        taken_at: this.#state.taken_at,
        quiz_version: QUIZ_VERSION,
        owner: this.#config.owner ?? null,
      });
    }
    if (!fits && !result.saved) {
      this.#resetSaveButton();
      this.#offerMessage(t.error);
      return;
    }
    this.#refreshOfferBody();
    this.#say(result.saved ? (this.#noCode ? t.savedNoCode : t.saved) : `${t.codeLabel} ${esc(offer?.code)}`);
  }
}

if (!customElements.get('bada-skin-quiz-component')) {
  customElements.define('bada-skin-quiz-component', BadaSkinQuizComponent);
}
